import {
	createWritingState,
	DEFAULT_WRITING,
	localWritingStore,
	type KeysKind,
	type WriteWith,
	type WritingPrefs,
	type WritingStore,
} from "@aybuben/client/device/writing.ts";
import type { LayoutId } from "@aybuben/core/writing/layouts.ts";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useStore } from "zustand";

import { capture } from "../../capture.ts";

/** What a writing step needs to know about how this learner writes, and the means to change it. */
export interface WritingApi extends WritingPrefs {
	layout: LayoutId;
	/** Whether the in-app keyboard's layout hint has been shown and dismissed. */
	hinted: boolean;
	/** Lowercase letters the learner has met; the in-app keyboard dims the rest. null dims none. */
	known: ReadonlySet<string> | null;
	setWrite(w: WriteWith): void;
	setKeys(k: KeysKind): void;
	dismissHint(): void;
}

/**
 * Without a provider a step writes with tiles and nothing can change that:
 * the state the app had before keyboards, which is what exercise tests and
 * any screen that mounts a view on its own get.
 */
const FALLBACK: WritingApi = {
	...DEFAULT_WRITING,
	layout: "phonetic",
	hinted: true,
	known: null,
	setWrite: () => {},
	setKeys: () => {},
	dismissHint: () => {},
};

export const WritingContext = createContext<WritingApi>(FALLBACK);

export const useWriting = (): WritingApi => useContext(WritingContext);

interface WritingProviderProps {
	layout: LayoutId;
	hinted: boolean;
	known: ReadonlySet<string> | null;
	/** Records the account preference; the hint flag syncs like `sound`. */
	onHinted: () => void;
	store?: WritingStore;
	children: ReactNode;
}

export function WritingProvider({
	layout,
	hinted,
	known,
	onHinted,
	store = localWritingStore,
	children,
}: WritingProviderProps) {
	const state = useMemo(
		() =>
			// Which input learners choose decides where typing goes next.
			createWritingState(store, (next) => capture("writing_input_changed", { ...next })),
		[store],
	);
	const { prefs, setWrite, setKeys } = useStore(state);
	const api = useMemo<WritingApi>(
		() => ({ ...prefs, layout, hinted, known, setWrite, setKeys, dismissHint: onHinted }),
		[prefs, layout, hinted, known, setWrite, setKeys, onHinted],
	);
	return <WritingContext.Provider value={api}>{children}</WritingContext.Provider>;
}
